<script module>
  import AppLayout from '$lib/layouts/AppLayout.svelte';
  export const layout = AppLayout;
</script>

<script lang="ts">
  import { router, usePage } from "@inertiajs/svelte";
  import * as Table from "$lib/components/ui/table";
  import { Button } from "$lib/components/ui/button";
  import { Input } from "$lib/components/ui/input";
  import * as Dialog from "$lib/components/ui/dialog";
  import { appStore } from '$lib/stores/appStore';
  import { Plus, Edit3, Trash2, FileText, CheckCircle, EyeOff, FileSpreadsheet, ChevronLeft, ChevronRight, Link, Copy, Check, QrCode } from "lucide-svelte";
  import { toast } from "svelte-sonner";
  import QRCode from '@castlenine/svelte-qrcode';
  import * as Popover from "$lib/components/ui/popover/index.js";

  // Svelte 5 Props & Page State
  let { short_urls = [] } = $props();

  const page = usePage();
  const translations = $derived((page.props as any).translations || {});
  const t = $derived(translations.short_urls || {}); 

  // Modal & Form State
  let isOpen = $state(false);
  let editingId = $state<number | null>(null);
  let form = $state({
    target_url: ''
  });

  // 记录最近复制成功的 item.id，用于按钮文案/图标反馈
  let copiedId = $state<number | string | null>(null);

  const BASE_URL = 'https://fenti.me';

  // 复制短链接到剪贴板
  async function copyToClipboard(code: string, id: number | string) {
    const fullUrl = `${BASE_URL}/${code}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      copiedId = id;
      toast.success(t.copied || '已复制到剪贴板');
      setTimeout(() => {
        copiedId = null;
      }, 2000);
    } catch (err) {
      toast.error(t.copy_failed || '复制失败');
    }
  }

  // 打开新建弹窗
  function openCreateModal() {
    editingId = null;
    form.target_url = '';
    isOpen = true;
  }

  // 打开编辑弹窗
  function openEditModal(item: any) {
    editingId = item.id;
    form.target_url = item.target_url;
    isOpen = true;
  }

  // 提交处理：根据 editingId 区分 PATCH 或 POST
  function handleSubmit(e: SubmitEvent) {
    e.preventDefault();

    if (editingId) {
      router.patch(`/short_urls/${editingId}`, { short_url: form }, {
        onSuccess: () => {
          isOpen = false;
          editingId = null;
          form.target_url = '';
        }
      });
    } else {
      router.post('/short_urls', { short_url: form }, {
        onSuccess: () => {
          isOpen = false;
          form.target_url = '';
        }
      });
    }
  }

  function handleDelete(id: number | string) {
    if (confirm(t.delete_confirm)) {
      router.delete(`/short_urls/${id}`);
    }
  }

  $effect(() => {
    const title = t.title;
    appStore.setTitle(title);
  });
</script>

<div class="p-8 max-w-6xl mx-auto">
  <header class="flex justify-between items-center mb-8  dark:border-zinc-800 pb-6">
    <div>
      <h1 class="text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 flex items-center gap-2.5">
        <Link class="size-5 text-zinc-800 dark:text-zinc-200" /> {t.title || '短链接管理'}
      </h1>
      <p class="text-sm text-muted-foreground mt-1">
        {t.subtitle || '创建与管理您的自定义短链接，跟踪点击与访问数据。'}
      </p>
    </div>
    
    <Button onclick={openCreateModal}>
      <Plus class="size-4" /> {t.create || '新建短链接'}
    </Button>
  </header>

  <!-- 新建 / 编辑 Dialog -->
  <Dialog.Root bind:open={isOpen}>
    <Dialog.Content>
      <Dialog.Header>
        <Dialog.Title>
          {editingId ? (t.edit || '编辑短链接') : (t.create || '新建短链接')}
        </Dialog.Title>
      </Dialog.Header>
      <form onsubmit={handleSubmit} class="space-y-4">
        <div>
          <label class="text-sm font-medium">{t.target_url || '目标链接'}</label>
          <Input type="url" bind:value={form.target_url} placeholder="https://example.com" required />
        </div>
        <div class="flex justify-end gap-2">
          <Button type="button" variant="outline" onclick={() => isOpen = false}>Cancel</Button>
          <Button type="submit">Submit</Button>
        </div>
      </form>
    </Dialog.Content>
  </Dialog.Root>

  <!-- 列表 Table -->
  <div class="border rounded-lg">
    <Table.Root>
      <Table.Header>
        <Table.Row>
          <Table.Head>{t.code || '短码'}</Table.Head>
          <Table.Head>{t.full_url || '完整短链接'}</Table.Head>
          <Table.Head>{t.target_url || '目标链接'}</Table.Head>
          <Table.Head>{t.clicks || '点击量'}</Table.Head>
          <Table.Head>{t.actions || '操作'}</Table.Head>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {#each short_urls as item (item.id)}
          <Table.Row>
            <Table.Cell class="font-mono font-bold text-primary">{item.code}</Table.Cell>
            
            <!-- 完整短链接地址与复制按钮 -->
            <Table.Cell>
              <div class="flex items-center gap-2">
                <a 
                  href="{BASE_URL}/{item.code}" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  class="font-mono text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {BASE_URL}/{item.code}
                </a>
                <Button 
                  size="icon" 
                  variant="ghost" 
                  class="size-4"
                  onclick={() => copyToClipboard(item.code, item.id)}
                  title={t.copy || "复制短链接"}
                >
                  {#if copiedId === item.id}
                    <Check class="size-3.5 text-green-600" />
                  {:else}
                    <Copy class="size-3.5 text-muted-foreground" />
                  {/if}
                </Button>


                <Popover.Root>
                  <Popover.Trigger class="inline-flex items-center justify-center size-5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors cursor-pointer" title={t.show_qrcode || '显示网站二维码'}>
                    <QrCode class="size-3.5" />
                  </Popover.Trigger>
                  
                  <Popover.Content class="w-48 p-3 rounded-xl border bg-popover text-popover-foreground shadow-md z-50">
                    <div class="flex flex-col items-center gap-2">
                      <div class="size-36 bg-white p-1 rounded-lg border border-border flex items-center justify-center">
                        <QRCode data="{BASE_URL}/{item.code}" isResponsive />
                      </div>
                      <span class="text-[10px] text-muted-foreground text-center truncate w-full">
                        { t.qrcode_title || 'Scan to Visit Site' }
                      </span>
                    </div>
                  </Popover.Content>
                </Popover.Root>
              </div>
            </Table.Cell>

            <Table.Cell class="max-w-xs truncate" title={item.target_url}>{item.target_url}</Table.Cell>
            <Table.Cell>{item.clicks_count}</Table.Cell>
            <Table.Cell class="space-x-2">
              <Button size="sm" variant="outline" onclick={() => openEditModal(item)}>
                {t.edit || '编辑'}
              </Button>
              <Button size="sm" variant="outline" onclick={() => router.visit(`/short_urls/${item.id}`)}>
                {t.analytics || '访问数据统计'}
              </Button>
              <Button size="sm" variant="destructive" onclick={() => handleDelete(item.id)}>
                {t.delete || 'Delete'}
              </Button>
            </Table.Cell>
          </Table.Row>
        {/each}
      </Table.Body>
    </Table.Root>
  </div>
</div>